import React, { useState, useEffect } from "react";
import "firebase/database";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from "@material-ui/core";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormGroup from "@mui/material/FormGroup";
import Slider from "@mui/material/Slider";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControl from "@mui/material/FormControl";
import FormLabel from "@mui/material/FormLabel";
import _ from "lodash";
import { useCompany } from "../../contexts/CompanyContext";
import { supabase } from "../../supabase";
import {
  upsertTrainingMaster,
  clearProductTypeIndex,
  upsertProductTypeIndex,
  clearSupplierIndex,
  upsertSupplierIndex,
  markTrainingAsDeleted,
} from "./sbTrainingIndex.functions";

/**
 * This component is a form for creating or editing a training module.
 * It fetches the module by its ID from the "training_master" table and
 * displays the module details. The user can edit the module details and
 * select applicable product types and suppliers (optional).
 * The form also includes a toggle for the module status and a field for
 * marks and pass threshold.
 * The component handles the creation and updating of the module in the
 * database.
 * @param {object} props - component props
 * @param {boolean} props.openDialog - Whether the dialog is open or not
 * @param {Function} props.handleDialogClose - Function to close the dialog
 * @param {number} props.moduleId - The ID of the module to be edited (or undefined if creating a new module)
 * @param {Array} props.trainingTypes - An array of training types
 * @param {Function} props.handleOpenMessage - Function to open a message with a message and a type
 */
export default function UpsertTrainingModule({
  openDialog,
  handleDialogClose,
  moduleId = undefined,
  trainingTypes,
  handleOpenMessage,
}) {
  const { productTypes, activeSuppliers, leaderLevels } = useCompany();
  const defModuleValues = {
    active: true,
    is_compulsory: true,
    marks: 0,
    module_category: "",
    module_description: "",
    module_name: "",
    pass_threshold: 100,
    is_deleted: false,
    training_types: 1,
    refs_structure_level_id: null,
    questions: [],
    videos: [],
  };

  const [moduleLoading, setModuleLoading] = useState(true);
  const [moduleValues, setModuleValues] = useState(defModuleValues);
  const [selectedProductTypes, setSelectedProductTypes] = useState([]);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [creatingModuleLoading, setCreatingModuleLoading] = useState(false);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const setDefaultFormStates = () => {
    setModuleValues(defModuleValues);
    setSelectedProductTypes([]);
    setSelectedSuppliers([]);
  };

  /**
   * Fetches a training module by its ID from the "training_master" table.
   * The function retrieves details of the module, including related product types
   * and suppliers, and updates the module values state.
   *
   * @returns {Promise<void>} - Resolves when the module data is fetched and state is updated.
   * @throws Will throw an error if the data fetching fails.
   */
  const fetchModule = async () => {
    const { data, error } = await supabase
      .schema(process.env.REACT_APP_SB_SCHEMA)
      .from("training_master")
      .select(
        "*, training_refs_product_type_index(refs_product_type(id, name), is_active), training_supplier_index(suppliers_master(id, supplier_name), is_active)"
      )
      .eq("id", moduleId);

    if (error) {
      console.error(error);
      throw error.message;
    }

    setModuleValues(data[0]);
    setModuleLoading(false);
  };

  useEffect(() => {
    if (moduleId) {
      fetchModule();
    } else {
      setDefaultFormStates();
      setModuleLoading(false);
    }
  }, [moduleId]);

  // destructuring seperate tables
  const { training_refs_product_type_index, training_supplier_index } =
    moduleValues;

  useEffect(() => {
    if (training_refs_product_type_index) {
      const productTypeIndex = training_refs_product_type_index
        .filter(({ refs_product_type, is_active }) => {
          return refs_product_type?.id && is_active;
        })
        .map(({ refs_product_type }) => refs_product_type.id);

      setSelectedProductTypes(productTypeIndex);
    }
  }, [training_refs_product_type_index]);

  useEffect(() => {
    if (training_supplier_index) {
      const supplierIndex = training_supplier_index
        .filter(({ suppliers_master, is_active }) => {
          return suppliers_master?.id && is_active;
        })
        .map(({ suppliers_master }) => suppliers_master.id);

      setSelectedSuppliers(supplierIndex);
    }
  }, [training_supplier_index]);

  // Handle changes in the input fields for module details
  const handleModuleValuesChange = event => {
    const { name, value } = event.target;
    setModuleValues(prevState => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleModuleValueRadio = (key, value) => {
    setModuleValues(prevState => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleSelectProductType = ptId => {
    if (selectedProductTypes.includes(ptId)) {
      setSelectedProductTypes(prev =>
        prev.filter(productType => productType !== ptId)
      );
    } else {
      setSelectedProductTypes(prev => {
        return [...prev, ptId];
      });
    }
  };

  const handleSelectSupplier = supId => {
    if (selectedSuppliers.includes(supId)) {
      setSelectedSuppliers(prev => prev.filter(supplier => supplier !== supId));
    } else {
      setSelectedSuppliers(prev => [...prev, supId]);
    }
  };

  // CRUD Handler Functions

  const handleCreateModule = async () => {
    /**
     * 1. Upload to training master
     * 2. Wait for id
     * 3. Upload to training_refs_product_type_index
     * 4. Upload to training_suppliers_master
     */

    setCreatingModuleLoading(true);

    try {
      // 1. Upload to training master

      const upsertResult = await upsertTrainingMaster(moduleValues);
      if (upsertResult.error !== 0) {
        throw new Error(`Failed at upsertTrainingMaster: ${upsertResult}`);
      }

      // Wait for id
      const { id: newModuleId } = await upsertResult.data[0];

      // 3. Upload to training_refs_product_type_index
      const upsertPTDatesResult = await upsertProductTypeIndex(
        newModuleId,
        selectedProductTypes
      );
      if (upsertPTDatesResult !== 0) {
        throw new Error(
          `Failed at upsertProductTypeDates: ${upsertPTDatesResult}`
        );
      }

      // 4. Upload to training_suppliers_master
      const upsertSupResult = await upsertSupplierIndex(
        newModuleId,
        selectedSuppliers
      );
      if (upsertSupResult !== 0) {
        throw new Error(`Failed at upsertSupplierIndex: ${upsertSupResult}`);
      }
      handleOpenMessage("Module successfully created", "success");
    } catch (error) {
      console.error("Error in training sequence:", error.message);
      handleOpenMessage("Error creating module", "error");
    }
    setCreatingModuleLoading(false);
    setDefaultFormStates();
    handleDialogClose();
  };

  const handleEditModule = async () => {
    /**
     * 1. Fix schema
     * 2. Upload to trianing_master
     * 3. clear all is_active flags on index tables
     * 4. upsert new values to index tables
     */

    setCreatingModuleLoading(true);

    // 1. fix schema
    const fixedModuleValues = _.omit(moduleValues, [
      "training_refs_product_type_index",
      "training_supplier_index",
    ]);

    try {
      // 2. Upload to trianing_master

      const upsertResult = await upsertTrainingMaster(fixedModuleValues);
      if (upsertResult.error !== 0) {
        throw new Error(`Failed at upsertTrainingMaster: ${upsertResult}`);
      }

      // 3.1 clear all is_active flags on training_refs_product_type_index

      const clearPTResult = await clearProductTypeIndex(moduleId);
      if (clearPTResult !== 0) {
        throw new Error(`Failed at clearProductTypeDates: ${clearPTResult}`);
      }

      // 3.1 clear all is_active flags on training_refs_product_type_index

      const clearSupResult = await clearSupplierIndex(moduleId);
      if (clearSupResult !== 0) {
        throw new Error(`Failed at clearSupplierIndex: ${clearSupResult}`);
      }

      // 4.1 upsert all selectedProductTypes to training_refs_product_type_index
      const upsertPTDatesResult = await upsertProductTypeIndex(
        moduleId,
        selectedProductTypes
      );
      if (upsertPTDatesResult !== 0) {
        throw new Error(
          `Failed at upsertProductTypeDates: ${upsertPTDatesResult}`
        );
      }

      // 4.2 upsert all selectedProductTypes to training_supplier_index
      const upsertSupResult = await upsertSupplierIndex(
        moduleId,
        selectedSuppliers
      );
      if (upsertSupResult !== 0) {
        throw new Error(`Failed at upsertSupplierIndex: ${upsertSupResult}`);
      }
      handleOpenMessage("Module successfully updated", "info");
    } catch (error) {
      console.error("Error in training sequence:", error.message);
      handleOpenMessage("Error updating module", "error");
    }

    setCreatingModuleLoading(false);
    setDefaultFormStates();
    handleDialogClose();
  };

  const handleDeleteModule = async () => {
    setCreatingModuleLoading(true);

    try {
      const deleteResult = await markTrainingAsDeleted(moduleId);
      if (deleteResult !== 0) {
        throw new Error(`Failed at upsertTrainingMaster: ${deleteResult}`);
      }
      handleOpenMessage("Module successfully deleted", "error");
    } catch (error) {
      console.error("Error in training sequence:", error.message);
      handleOpenMessage("Error deleting module", "error");
    }

    setCreatingModuleLoading(false);
    setDefaultFormStates();
    handleDialogClose();
    setShowConfirmationDialog(false);
  };

  const productTypeMap = productTypes?.map(type => {
    const ptChecked = selectedProductTypes?.includes(type.id);
    return (
      <FormControlLabel
        key={type.id}
        control={
          <Checkbox
            checked={ptChecked}
            onChange={() => handleSelectProductType(type.id)}
            name={type.name}
          />
        }
        label={type.name}
      />
    );
  });

  const supplierMap = activeSuppliers?.map(supplier => {
    const supChecked = selectedSuppliers?.includes(supplier.id);
    return (
      <FormControlLabel
        key={supplier.id}
        control={
          <Checkbox
            checked={supChecked}
            name={supplier.supplier_name}
            onChange={() => handleSelectSupplier(supplier.id)}
          />
        }
        label={supplier.supplier_name}
      />
    );
  });

  return (
    <div>
      <Dialog
        maxWidth="lg"
        fullWidth
        open={openDialog}
        onClose={() => {
          setDefaultFormStates();
          handleDialogClose();
        }}
        PaperProps={{
          style: {
            borderRadius: "16px",
            boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        <DialogTitle>
          {moduleId ? "Edit Module" : "Create New Module"}
        </DialogTitle>
        {moduleLoading ? (
          <DialogContent>Loading</DialogContent>
        ) : (
          <DialogContent>
            <TextField
              style={{ marginBottom: 10 }}
              fullWidth
              variant="outlined"
              label="Module Name"
              name="module_name"
              value={moduleValues?.module_name}
              onChange={handleModuleValuesChange}
            />
            <TextField
              style={{ marginBottom: 10 }}
              fullWidth
              variant="outlined"
              label="Module Description"
              name="module_description"
              value={moduleValues?.module_description}
              onChange={handleModuleValuesChange}
            />
            <TextField
              style={{ marginBottom: 10 }}
              fullWidth
              variant="outlined"
              label="Module Category"
              name="module_category"
              value={moduleValues?.module_category}
              onChange={handleModuleValuesChange}
            />
            <div style={{ marginBottom: 10 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">Toggle Status</FormLabel>
                <RadioGroup
                  aria-label="status"
                  name="active"
                  value={moduleValues?.active}
                  onChange={e => {
                    handleModuleValueRadio("active", e.target.value);
                  }}
                >
                  <FormControlLabel value control={<Radio />} label="Active" />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="Inactive"
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <div style={{ marginBottom: 10 }}>
              <FormControl component="fieldset">
                <FormLabel component="legend">
                  Compulsory (Blocks Access)
                </FormLabel>
                <RadioGroup
                  aria-label="is_compulsory"
                  name="is_compulsory"
                  value={moduleValues?.is_compulsory}
                  onChange={e => {
                    handleModuleValueRadio("is_compulsory", e.target.value);
                  }}
                >
                  <FormControlLabel value control={<Radio />} label="Yes" />
                  <FormControlLabel
                    value={false}
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </FormControl>
            </div>
            <div style={{ marginTop: 20 }}>
              <Typography style={{ marginBottom: 20 }}>
                Training Type:
              </Typography>
              <FormGroup>
                {trainingTypes.map(type => (
                  <FormControlLabel
                    key={type.id}
                    control={
                      <Radio
                        checked={moduleValues?.training_types === type.id}
                        onChange={() => {
                          handleModuleValueRadio("training_types", type.id);
                          if (type.id !== 3) {
                            handleModuleValueRadio(
                              "refs_structure_level_id",
                              null
                            );
                          } else {
                            handleModuleValueRadio(
                              "refs_structure_level_id",
                              1
                            );
                          }
                        }}
                        name={type.type}
                      />
                    }
                    label={type.type}
                  />
                ))}
              </FormGroup>
            </div>

            {moduleValues?.training_types === 3 && (
              <div style={{ marginTop: 20 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">Leader Levels:</FormLabel>
                  <RadioGroup
                    aria-label="status"
                    name="active"
                    value={moduleValues?.refs_structure_level_id}
                    onChange={e => {
                      handleModuleValueRadio(
                        "refs_structure_level_id",
                        parseInt(e.target.value)
                      );
                    }}
                  >
                    {leaderLevels?.map(type => (
                      <FormControlLabel
                        value={type.id}
                        control={<Radio />}
                        label={type.name}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </div>
            )}
            <div style={{ marginTop: 20 }}>
              <Typography style={{ marginBottom: 20 }}>
                Applicable Product Types (Optional):
              </Typography>
              <FormGroup>{productTypeMap}</FormGroup>
            </div>
            <div style={{ marginTop: 20 }}>
              <Typography style={{ marginBottom: 20 }}>
                Applicable Suppliers (Optional):
              </Typography>
              <FormGroup>{supplierMap}</FormGroup>
            </div>
            <TextField
              style={{ marginBottom: 10, marginTop: 20 }}
              fullWidth
              variant="outlined"
              label="Marks"
              name="marks"
              value={moduleValues?.marks}
              onChange={handleModuleValuesChange}
            />
            <div style={{ width: "60%", marginTop: 20, marginBottom: 20 }}>
              <Typography>
                % To Pass: {moduleValues?.pass_threshold}%
              </Typography>
              <Slider
                value={moduleValues?.pass_threshold}
                min={0}
                max={100}
                step={1}
                onChange={handleModuleValuesChange}
                name="pass_threshold"
                valueLabelDisplay="auto"
                valueLabelSuffix=" %"
              />
            </div>
          </DialogContent>
        )}

        <DialogActions>
          <Button
            disabled={creatingModuleLoading}
            onClick={() => {
              setDefaultFormStates();
              handleDialogClose();
            }}
            color="secondary"
          >
            Cancel
          </Button>
          {moduleId && (
            <Button
              disabled={creatingModuleLoading}
              onClick={() => setShowConfirmationDialog(true)}
              style={{ color: "red" }}
            >
              Delete
            </Button>
          )}
          {!moduleId ? (
            <Button
              disabled={creatingModuleLoading}
              onClick={handleCreateModule}
              color="primary"
            >
              {creatingModuleLoading ? "Loading..." : "Create"}
            </Button>
          ) : (
            <Button
              disabled={creatingModuleLoading}
              onClick={handleEditModule}
              color="primary"
            >
              {creatingModuleLoading ? "Loading..." : "Update"}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      {/* Confirmation Dialog */}
      <Dialog
        open={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
      >
        <DialogTitle>Delete Module</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this module?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmationDialog(false)}>
            Cancel
          </Button>
          <Button onClick={() => handleDeleteModule()}>Delete</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
